import api from "@/api";
import {
	db,
	enqueueCaptureEvidenceManifest,
	listDueCaptureEvidenceManifests,
	markCaptureEvidenceManifestRetry,
	markCaptureEvidenceManifestSubmitted,
	type FileUploadSession
} from "@/db/fileUploadDb";
import { newUploadId } from "@uploads";
import { isLoadedMeFeatureFlagEnabled } from "@/helpers/meFeatureFlags";

let drainInFlight: Promise<void> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let onlineListenerRegistered = false;

/**
 * LSP-18592 go-live: file-picker manifests ship dark. Only `me.config.featureFlags.capture_evidence`
 * turns them on, and a `me` that has not loaded yet reads as off, so nothing is attached, enqueued
 * or submitted until the API says so.
 */
export function isCaptureEvidenceEnabled(): boolean {
	return isLoadedMeFeatureFlagEnabled("capture_evidence");
}

export function createFilePickerCaptureEvidence(file: File): Record<string, unknown> {
	// newUploadId keeps the Safari 15 / iOS 15 support floor: it uses crypto.randomUUID when present
	// and derives a real UUID v4 from getRandomValues otherwise, never a Math.random stand-in.
	const sourceId = newUploadId();
	return {
		schemaVersion: 1,
		source: "file-picker",
		method: "file",
		sourceId,
		capturedAt: new Date().toISOString(),
		file: { name: file.name, size: file.size, mimeType: file.type || "application/octet-stream", lastModified: file.lastModified }
	};
}

export function drainCaptureEvidenceManifests(): Promise<void> {
	// Checked at call time. A queue left by an earlier session waits for the next upload, online event
	// or scheduled retry. With the flag off nothing is registered at all: the `online` listener is added
	// only the first time a drain runs with the flag on (LSP-18592 go-live: flag-off admin == develop).
	if (!isCaptureEvidenceEnabled()) return Promise.resolve();
	if (!onlineListenerRegistered && typeof window !== "undefined") {
		onlineListenerRegistered = true;
		window.addEventListener("online", () => void drainCaptureEvidenceManifests());
	}
	if (drainInFlight) return drainInFlight;
	drainInFlight = drain().finally(() => {
		drainInFlight = null;
	});
	return drainInFlight;
}

async function drain(): Promise<void> {
	const manifests = await listDueCaptureEvidenceManifests();
	for (const manifest of manifests) {
		await submit(manifest);
	}
	// Include rows that become due while a submission is in flight. Looking only above(now)
	// loses a late enqueue with retryAt=0 and leaves it stranded until the next startup/online event.
	const pending = await db.captureEvidenceManifests.toArray();
	const nextRetryAt = pending.reduce((earliest, row) => Math.min(earliest, row.retryAt), Number.POSITIVE_INFINITY);
	if (Number.isFinite(nextRetryAt)) {
		if (retryTimer) clearTimeout(retryTimer);
		retryTimer = setTimeout(
			() => {
				retryTimer = null;
				void drainCaptureEvidenceManifests().catch((error) => console.warn("[CaptureEvidence] Scheduled drain failed", error));
			},
			Math.max(250, nextRetryAt - Date.now())
		);
	}
}

export async function enqueueFileSessionCaptureEvidence(session: FileUploadSession): Promise<void> {
	if (!isCaptureEvidenceEnabled()) return;
	if (!session.captureEvidence || (session.fileCategory !== "image" && session.fileCategory !== "video")) return;
	await enqueueCaptureEvidenceManifest({
		conversationId: session.conversationId,
		sourceType: session.fileCategory === "video" ? "recording" : "snapshot",
		sourceClientId: session.id,
		manifest: { ...session.captureEvidence, sourceId: session.id }
	});
}

async function submit(manifest: Awaited<ReturnType<typeof listDueCaptureEvidenceManifests>>[number]): Promise<void> {
	try {
		await api.submitCaptureEvidenceManifest({
			conversationId: manifest.conversationId,
			sourceType: manifest.sourceType,
			sourceClientId: manifest.sourceClientId,
			sourceRevision: "1",
			clientSchemaVersion: 1,
			manifest: manifest.manifest
		});
		await markCaptureEvidenceManifestSubmitted(manifest.id);
	} catch (error) {
		await markCaptureEvidenceManifestRetry(manifest.id, error);
	}
}
